import React, { useState } from 'react';
import {
  Box,
  Link as DSLink,
  ListRow,
  Typography,
} from '@saviynt/design-system';
import { format } from 'date-fns';

import { CF_NEU_BLD } from '../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';
import { SodColumnChip } from '../index';

const getFormattedValue = (
  value: string | number | null | undefined,
  dateFormat: string
): string => {
  if (!value) {
    return '';
  }

  // Try to parse as date if it looks like a date string
  if (typeof value === 'string' && value.includes('GMT')) {
    try {
      const date = new Date(value);

      if (!Number.isNaN(date.getTime())) {
        return format(date, dateFormat);
      }
    } catch (e) {
      // If parsing fails, return the original string
    }
  }

  // Return as is if it's not a date or parsing failed
  return String(value);
};

interface ExpandableBlockProps {
  items: { value: string }[][];
  initialDisplayCount?: number;
}

/**
 * ExpandableBlock
 */
function ExpandableBlock({
  items,
  initialDisplayCount = 2,
}: ExpandableBlockProps) {
  const l = useInvertedLocalize();
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    aag: {
      global: { globalDateFormat },
    },
  } = useTypedSelector((state) => state);

  const itemsToShow = isExpanded ? items : items.slice(0, initialDisplayCount);

  const remainingCount = items.length - initialDisplayCount;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const globalFormatWithTime = `${globalDateFormat}, HH:mm`;

  return (
    <div className='expandable-block'>
      {itemsToShow.map((itemGroup, index) => {
        // Since items only have 'value' property, we'll use array indices to identify different fields
        const nameItem = itemGroup[0];
        const startItem = itemGroup[1];
        const endItem = itemGroup[2];
        const addedByItem = itemGroup[3];
        const commentsItem = itemGroup[4];

        return (
          <ListRow
            key={nameItem.value || `row-${index}`}
            className='SodSidePanelHistory-postBlock-ListRow'
            dataTestId='testId'
            isCheckboxDisabled
            centerContent={
              <Box className='SodSidePanelHistory-postBlock-centerContent'>
                <Typography
                  kind='h3'
                  desktop='h3-d'
                  mobile='h3-d'
                  color={CF_NEU_BLD}>
                  {nameItem.value || ''}
                </Typography>
                <Box className='SodSidePanelHistory-postBlock-centerContent-chip'>
                  <SodColumnChip
                    name={l('Start')}
                    value={getFormattedValue(
                      startItem.value,
                      globalFormatWithTime
                    )}
                    isPrefixInterpoint
                  />

                  <SodColumnChip
                    name={l('End')}
                    value={getFormattedValue(
                      endItem.value,
                      globalFormatWithTime
                    )}
                    isPrefixInterpoint
                  />

                  <SodColumnChip
                    name={l('Added by')}
                    value={addedByItem.value || ''}
                    isPrefixInterpoint
                  />
                  <SodColumnChip
                    name={l('Comments')}
                    value={commentsItem.value || '-'}
                    isPrefixInterpoint={false}
                  />
                </Box>
              </Box>
            }
          />
        );
      })}

      {items.length > initialDisplayCount && (
        <Box tag='div' className=''>
          {isExpanded ? (
            <DSLink
              text={l('Show Less')}
              kind='button'
              onClick={toggleExpanded}
              suffixIconKind='ChevronUpSmall'
            />
          ) : (
            <DSLink
              text={l('+{count} More', { count: remainingCount })}
              kind='button'
              onClick={toggleExpanded}
              suffixIconKind='ChevronDownSmall'
            />
          )}
        </Box>
      )}
    </div>
  );
}

ExpandableBlock.defaultProps = {
  initialDisplayCount: 2,
};

export default ExpandableBlock;
